// One place, named in a URL.
//
// #53 made the view shareable — where the map is and which layers are on.
// This is the other half: a single stop, station or feature, so a reader can
// send "here is my stop, see when the next one is" rather than a hostname and
// directions for panning.
//
//   ?go=stop:6390012345     a baked stop — bus, tram or Subway
//   ?go=rail:ABD            a rail station pin, by CRS
//   ?go=pin:bus-station     any other fixed pin, by kind
//   ?go=feature:cameras/17  anything the live layers draw
//
// The first three are the map's fixed points — a stop, a station, a harbour, a
// bus station are all still there tomorrow — which is exactly why they are the
// ones worth a permanent link. Features come and go, and `?go=feature:` is
// kept for the FUBAR markers that predate all of this.
//
// `?marker=<id>` still works and is unchanged. Links to it have been pasted
// into Facebook posts since FUBAR shipped, and a share link that stops working
// is worse than one that never existed.
//
// The URL, both directions: reading a target out of one and writing an open
// board back into one, so the share button can stay ignorant of boards and
// keep reading nothing but location.href.
//
// What to DO with a target — reveal its layer, fly, open its board — lives in
// main.ts beside the things that can do it, and the reveal is the part that
// must not be skipped: the stops layers ship off, so flying to a stop without
// switching one on lands the reader on an empty street.

export type TargetKind = 'stop' | 'rail' | 'pin' | 'feature';

export interface Target {
  readonly kind: TargetKind;
  readonly id: string;
}

const KINDS: readonly TargetKind[] = ['stop', 'rail', 'pin', 'feature'];

/**
 * The fixed points of the map — the targets worth a link that outlives a day.
 *
 * Used to decide whether the open board offers a share button at all: a
 * roadworks list or a camera control room is a view of something passing, and
 * a link to it would be a link to nothing by the weekend.
 */
export function isFixed(target: Target | null): boolean {
  return target !== null && target.kind !== 'feature';
}

/**
 * The target named in a query string, or null.
 *
 * `?marker=` is read as a feature, which is what it always meant — FUBAR
 * markers are features like any other, and the parameter predates there being
 * a general way to say so.
 *
 * Never throws: a malformed URL should leave the reader on the map rather than
 * on a blank page.
 */
export function parseTarget(search: string): Target | null {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search);
  } catch {
    return null;
  }

  const go = params.get('go');
  if (go) {
    const at = go.indexOf(':');
    // A prefix is required rather than guessed at. An id with no kind could be
    // a stop or a feature, and resolving it by trying both would make the
    // answer depend on which list happened to contain a collision.
    if (at < 1) return null;
    const kind = go.slice(0, at) as TargetKind;
    const id = go.slice(at + 1);
    if (!KINDS.includes(kind) || !id) return null;
    return { kind, id };
  }

  const marker = params.get('marker');
  return marker ? { kind: 'feature', id: marker } : null;
}

/** The `?go=` value for a target, for building a link to share. */
export function targetToParam(target: Target): string {
  return `${target.kind}:${target.id}`;
}

/**
 * The ids a feature target should be looked up under.
 *
 * A FUBAR marker is stored with a `fubar:` prefix but shared without one, so
 * both spellings have to resolve or every marker link ever posted breaks. The
 * bare id is tried first: it is what a `?go=feature:` link carries, and the
 * prefixed form is the older `?marker=` shape.
 */
export function featureIdCandidates(id: string): string[] {
  return id.startsWith('fubar:') ? [id] : [id, `fubar:${id}`];
}

/**
 * A query string with the target named in it, or with it taken back out.
 *
 * The half that makes a stop *shareable* rather than merely linkable: the
 * share button reads `location.href` and knows nothing else, so the address
 * bar has to already say which board is open. Returns the leading `?`, or an
 * empty string when nothing is left — the shape history.replaceState wants.
 *
 * Every other parameter is passed through untouched, and that is the whole
 * difficulty. Two writers of one query string that each rebuild it from their
 * own state take turns deleting each other's half, and each looks perfectly
 * correct read on its own: this and selectionToSearch() run on the same URL,
 * one owns `go` and the other owns `on`/`off`, and neither may assume it wrote
 * last. `marker` is the single exception — it is the older spelling of
 * `go=feature:`, so leaving it in place would hand out a URL naming two
 * different things and let parseTarget's precedence decide which one won.
 */
/**
 * Serialised the way somebody would want to read it back.
 *
 * `?go=stop%3A6390012345` is correct and it is unreadable, on a URL whose
 * entire purpose is being pasted into a message. Both characters are legal
 * sub-delimiters in a query value (RFC 3986) and both read back identically —
 * `new URLSearchParams('a=x:y').get('a')` is 'x:y' — so this is a display
 * choice with no parsing consequence, for the parameters here or any other
 * that happens to contain one. selectionToSearch() makes the same trade for
 * the comma in the `on`/`off` lists, which both writers here preserve.
 *
 * The empty string rather than a bare `?` when nothing is left: this is handed
 * to history.replaceState as `${pathname}${this}${hash}`, so a stray question
 * mark would sit in the address bar of every reader who closed a board.
 */
function readableQuery(params: URLSearchParams): string {
  const query = params.toString().replace(/%3A/g, ':').replace(/%2C/g, ',');
  return query ? `?${query}` : '';
}

export function withTarget(search: string, target: Target | null): string {
  const params = new URLSearchParams(search);
  params.delete('go');
  params.delete('marker');
  if (target) params.set('go', targetToParam(target));
  return readableQuery(params);
}

/**
 * The route drawn alongside the target, or null.
 *
 * A companion parameter rather than a fourth `go=` kind, because a route is
 * never the only thing on screen: it is drawn from a row on a board, so a link
 * that named the route alone would throw away the stop the sharer was standing
 * at. `?go=stop:6390012345&route=3841` is both, which is what they were
 * actually looking at.
 */
export function parseRoute(search: string): string | null {
  try {
    return new URLSearchParams(search).get('route') || null;
  } catch {
    return null;
  }
}

/**
 * A query string with the drawn route named in it, or with it taken back out.
 *
 * The third writer of this one string, after withTarget() and
 * selectionToSearch(). Each owns its own parameters and passes every other one
 * through untouched; none may assume it wrote last. See withTarget() for why
 * that is the hard part, and test-deep-link.mjs, which runs them in every
 * order rather than trusting the argument.
 */
export function withRoute(search: string, route: string | null): string {
  const params = new URLSearchParams(search);
  params.delete('route');
  if (route) params.set('route', route);
  return readableQuery(params);
}
